import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Phone, Star, ChevronDown, Wrench, Hammer, Ruler, Drill, PaintRoller } from "lucide-react";
import { MaskedLine, FadeIn } from "./Reveal";
import { IMAGES } from "../../lib/images";
import { scrollToId } from "../../lib/scroll";

export default function Hero() {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
    const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const bgY = useTransform(scrollYProgress, [0, 1], [0, 160]);

    return (
        <section ref={ref} className="relative flex min-h-screen items-center overflow-hidden pb-16 pt-28">
            <motion.div style={{ y: bgY }} className="pointer-events-none absolute inset-0 text-ink/[0.05]" aria-hidden>
                <Wrench className="absolute right-[6%] top-[10%] h-44 w-44 -rotate-12" strokeWidth={0.8} />
                <Hammer className="absolute left-[36%] top-[5%] h-28 w-28 rotate-12" strokeWidth={0.8} />
                <Ruler className="absolute bottom-[16%] left-[3%] h-40 w-40 -rotate-6" strokeWidth={0.8} />
                <Drill className="absolute bottom-[10%] right-[36%] h-32 w-32 rotate-6" strokeWidth={0.8} />
                <PaintRoller className="absolute left-[52%] top-[44%] h-24 w-24 rotate-12" strokeWidth={0.8} />
            </motion.div>

            <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
                <div className="lg:col-span-7">
                    <FadeIn y={0} delay={0.05}>
                        <div className="mb-8 flex items-center gap-3">
                            <span className="h-px w-10 bg-gold" />
                            <span className="text-xs font-semibold uppercase tracking-[0.3em] text-ink/60">
                                Handy&rsquo;s — remonty Kraków i okolice
                            </span>
                        </div>
                    </FadeIn>
                    <h1 className="font-display text-5xl font-black leading-[1.0] tracking-tighter text-ink sm:text-6xl lg:text-7xl">
                        <MaskedLine delay={0.15}>Kompleksowe usługi</MaskedLine>
                        <MaskedLine delay={0.28}>dla domu, mieszkań</MaskedLine>
                        <MaskedLine delay={0.41}>
                            i lokali<span className="text-gold">.</span>
                        </MaskedLine>
                    </h1>
                    <FadeIn delay={0.6}>
                        <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/60 md:text-lg">
                            Profesjonalne usługi remontowe, wykończeniowe, elektryczne oraz hydrauliczne.
                            Jedna firma — wszystkie prace wykonane solidnie i terminowo.
                        </p>
                    </FadeIn>
                    <FadeIn delay={0.75}>
                        <div className="mt-10 flex flex-wrap items-center gap-4">
                            <button
                                data-testid="hero-quote-btn"
                                onClick={() => scrollToId("kontakt")}
                                className="group inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-ink shadow-[0_10px_30px_-10px_rgba(255,179,0,0.6)] transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_18px_44px_-10px_rgba(255,179,0,0.7)]"
                            >
                                Umów wycenę
                                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                            </button>
                            <a
                                data-testid="hero-call-btn"
                                href="tel:+48512913717"
                                className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-8 py-4 font-display text-sm font-bold uppercase tracking-wider text-ink transition-[border-color,background-color,color] duration-300 hover:border-ink hover:bg-ink hover:text-white"
                            >
                                <Phone className="h-4 w-4" />
                                Zadzwoń teraz
                            </a>
                        </div>
                    </FadeIn>
                    <FadeIn delay={0.9}>
                        <div className="mt-12 flex flex-wrap items-center gap-4 text-sm text-ink/60">
                            <span className="flex gap-0.5" aria-label="Ocena 5 na 5">
                                {[...Array(5)].map((_, i) => (
                                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                                ))}
                            </span>
                            <span className="font-semibold text-ink">5,0</span>
                            <span className="h-4 w-px bg-ink/15" />
                            <span>100+ zrealizowanych projektów</span>
                        </div>
                    </FadeIn>
                </div>

                <div className="relative lg:col-span-5">
                    <FadeIn delay={0.4} y={40}>
                        <div className="relative">
                            <div className="absolute -inset-3 rotate-2 rounded-[2rem] border-2 border-gold/70" aria-hidden />
                            <motion.div style={{ y: imgY }} className="relative overflow-hidden rounded-[2rem] shadow-2xl">
                                <img
                                    src={IMAGES.hero}
                                    alt="Premium wykończenie wnętrza — Handy's"
                                    className="h-[420px] w-full object-cover md:h-[540px]"
                                    loading="eager"
                                />
                            </motion.div>
                            <div className="absolute -bottom-6 -left-3 rounded-2xl border border-ink/5 bg-white/85 px-6 py-4 shadow-xl backdrop-blur-xl md:-left-10">
                                <p className="font-display text-3xl font-black tracking-tight text-ink">100+</p>
                                <p className="text-xs font-semibold uppercase tracking-widest text-ink/50">realizacji</p>
                            </div>
                            <div className="absolute -top-5 right-6 rounded-full bg-ink px-5 py-2.5 text-xs font-bold uppercase tracking-widest text-white shadow-lg">
                                24h <span className="text-gold">szybki kontakt</span>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </div>

            <motion.button
                data-testid="hero-scroll-cue"
                onClick={() => scrollToId("zalety")}
                className="absolute bottom-6 left-1/2 -translate-x-1/2 text-ink/40 transition-colors duration-300 hover:text-gold"
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                aria-label="Przewiń w dół"
            >
                <ChevronDown className="h-6 w-6" />
            </motion.button>
        </section>
    );
}
